import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  MenuItemsProps,
} from '@headlessui/react';
import clsx from 'clsx';
import { ReactNode, useEffect } from 'react';

type DropdownMenuProps = {
  trigger: ReactNode;
  children: ReactNode;
  anchor?: MenuItemsProps['anchor'];
  className?: string;
  matchTriggerWidth?: true;
  onOpenChange?: (isOpen: boolean) => void;
};

export const DropdownMenu = (props: DropdownMenuProps) => {
  return (
    <Menu>{({ open }) => <DropdownMenuInner {...props} isOpen={open} />}</Menu>
  );
};

type DropdownMenuInnerProps = DropdownMenuProps & { isOpen: boolean };

const DropdownMenuInner = ({
  trigger,
  children,
  anchor = 'bottom end',
  className = '',
  matchTriggerWidth,
  onOpenChange,
  isOpen,
}: DropdownMenuInnerProps) => {
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  return (
    <>
      <MenuButton className="focus:outline-none">{trigger}</MenuButton>
      <MenuItems
        transition
        anchor={anchor}
        className={clsx(
          'data-closed:scale-95 data-closed:opacity-0 origin-top-right rounded bg-white p-1 text-sm/6 shadow-xl transition duration-100 ease-out [--anchor-gap:0.25rem] focus:outline-none',
          matchTriggerWidth && 'w-[--button-width]',
          className
        )}
      >
        {children}
      </MenuItems>
    </>
  );
};

type DropdownMenuItemProps = {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
};

export const DropdownMenuItem = ({
  children,
  onClick,
  disabled = false,
  className = '',
}: DropdownMenuItemProps) => {
  return (
    <MenuItem disabled={disabled}>
      <div
        onClick={onClick}
        role="menuitem"
        aria-disabled={disabled}
        className={clsx(
          'group flex w-full items-center gap-2 px-3 py-1.5',
          !disabled && 'cursor-pointer',
          disabled && 'cursor-not-allowed opacity-50',
          className
        )}
      >
        {children}
      </div>
    </MenuItem>
  );
};
